import {Button, InfoTooltip} from '@wandb/components';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {
  addUTCTimezoneIfNotPresent,
  monthRoundedTime,
} from '@wandb/weave/common/util/time';
import {Icon, IconOpenNewTab} from '@wandb/weave/components/Icon';
import {useRunTraceStats} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {
  getTagContrastColor,
  RemovableTag,
  RemoveAction,
} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {JSONNaN} from '@wandb/weave/core';
import {maybePluralizeWord} from '@wandb/weave/core/util/string';
import sanitizeBranchName from 'branch-safe-name/sanitize';
import copyToClipboard from 'copy-to-clipboard';
import _ from 'lodash';
import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Dropdown, Grid, Header, Image, Modal, Segment} from 'semantic-ui-react';

import CopyableText from '../../components/CopyableText';
import {InlineMarkdownEditor} from '../../components/elements/InlineMarkdownEditor';
import {TimeDisplay} from '../../components/elements/TimeDisplay';
import {useFetchRunJob} from '../../components/Launch/hooks';
import {JobDrawer} from '../../components/Launch/JobDrawer/JobDrawer';
import {Link as Link2} from '../../components/Launch/Link';
import {RunConfigTree} from '../../components/RunConfigTree';
import RunStateIndicator from '../../components/RunStateIndicator';
import {RunSummaryTree} from '../../components/RunSummaryTree';
import {RunTriggerEventsExport} from '../../components/RunTriggerEventsExport';
import {
  isParquetExportAllowed,
  RunTriggerParquetExport,
} from '../../components/RunTriggerParquetExport';
import {RunTriggerRecomputeKeysInfo} from '../../components/RunTriggerRecomputeKeysInfo';
import {ServiceAccountModal} from '../../components/ServiceAccountModal';
import {TagAddButton} from '../../components/TagAddButton';
import * as GQLTypes from '../../types/graphql';
import {useAdminModeActive} from '../../util/admin';
import {DateFormat, format} from '../../util/date';
import DOC_URLS from '../../util/doc_urls';
import {trackPageViewed} from '../../util/navigation';
import * as RunTypes from '../../util/runTypes';
import {checkIsServiceAccount} from '../../util/serviceAccounts';
import * as urls from '../../util/urls';
import {BenchmarkApproval} from '../Benchmark/BenchmarkApprovalDialogue';
import {RunArtifactGrid} from './RunArtifacts';
import RunPageHeader from './RunPageHeader';

interface RunOverviewProps {
  run: GQLTypes.RunPageRunQuery;
  project: GQLTypes.Project;
  viewer: GQLTypes.User;

  config: RunTypes.KeyVal;
  summaryMetrics: RunTypes.KeyVal;

  history: any;

  patchRun(fields: {
    displayName?: string;
    notes?: string;
    tags?: string[];
  }): void;

  updateProject(fields: {access?: string}): Promise<unknown>;

  deleteRun(id: string, deleteArtifacts?: boolean): void;
}

function isOffline(wandbValue: any) {
  // This is a helper function that de-codes the telemetry data provided in the config from the client
  // For details of the structure see: https://github.com/wandb/client/blob/master/wandb/proto/wandb_telemetry.proto
  // To summarize:  - `t` is the telemetry record
  //                - `3` is the feature field (in the telemetry record)
  //                - `4` is the offline flag (in the feature message)
  return !!wandbValue?.t?.[3]?.[4];
}

const RunOverview = React.memo(
  ({
    config,
    deleteRun,
    patchRun,
    project,
    run,
    summaryMetrics,
    viewer,
  }: RunOverviewProps) => {
    const {
      name,
      displayName,
      user,
      createdAt,
      tags,
      notes,
      host,
      benchmarkRun,
      sweep,
      runInfo,
      jobType,
      group,
      branchPoint,
    } = run;

    const isAdmin = useAdminModeActive();
    const runTraceStats = useRunTraceStats(
      project.entityName,
      project.name,
      run.name
    );

    // If there are traces for this run, display a link to them.
    const traceCount = runTraceStats.result?.count ?? 0;
    let tracesLink: string | null = null;
    if (traceCount > 0) {
      const filterValue = `${project.internalId}:${run.name}`;
      const runFilters = {
        items: [
          {
            field: 'wb_run_id',
            operator: '(string): equals',
            value: filterValue,
          },
        ],
      };
      const runCols = {
        wb_run_id: true,
      };
      // TODO: Constructing the URL this way isn't nice, but we don't have
      // access here to all of the routing context inside Weave.
      tracesLink = `/${project.entityName}/${
        project.name
      }/weave/traces?filters=${JSON.stringify(
        runFilters
      )}&cols=${JSON.stringify(runCols)}`;
    }

    // Get the job that created this run (if any)
    const {jobInfo} = useFetchRunJob({
      entityName: project.entityName,
      projectName: project.name,
      runName: run.name,
      skip: run?.inputArtifacts == null || run.inputArtifacts.totalCount === 0,
    });

    const [jobToRun, setJobToRun] = useState<string | null>(null);
    const cloneRun = (jobId: string) => {
      setJobToRun(jobId);
    };
    const closeDrawer = () => {
      setJobToRun(null);
    };

    // for page view tracking
    useEffect(() => {
      let originPageName = 'Run';
      if (group != null) {
        originPageName = 'Group run';
      } else if (sweep != null) {
        originPageName = 'Sweep run';
      }
      trackPageViewed(originPageName, 'Overview');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const wandb = config._wandb as any;

    let cliVersion;
    let spellUrl;
    let startUTCSeconds;
    let runIsOffline = false;
    if (wandb && wandb.value) {
      cliVersion = wandb.value.cli_version;
      spellUrl = wandb.value.spell_url;
      startUTCSeconds = wandb.value.start_time;
      runIsOffline = isOffline(wandb.value);
    }

    let createdTimeValue = createdAt;
    if (runIsOffline && startUTCSeconds) {
      createdTimeValue = new Date(startUTCSeconds * 1000)
        .toISOString()
        .split('.')[0];
    }

    const createdTime = format(
      new Date(addUTCTimezoneIfNotPresent(createdTimeValue)),
      DateFormat.MONTH_DAYTH_YEAR_HOUR_MIN_SECONDS
    );

    const readOnly = !viewer || project.readOnly;

    let benchmarkSummaryMetrics;
    if (benchmarkRun) {
      benchmarkSummaryMetrics = JSONNaN.JSONparseNaN(
        benchmarkRun.run.summaryMetrics
      );
    }

    const authorImage: JSX.Element = (
      <Image
        src={user.photoUrl}
        avatar
        onError={(i: any) => (i.target.style.display = 'none')}
      />
    );

    // account for non-team entity case
    const serviceAccountModalLink = project.entity.isTeam
      ? urls.teamSettings(project.entityName)
      : `/${project.entityName}`;

    const systemStats = [
      runInfo?.gpuCount,
      runInfo?.gpu,
      runInfo?.cpuCount,
      runInfo?.cpuCountLogical,
    ];
    const applySystemHardwareStyles = _.compact(systemStats).length > 1;

    const [showRaw, setShowRaw] = useState<{header: string; json: any}>();

    const viewRawSummary = useCallback(() => {
      setShowRaw({
        header: `Summary`,
        json: getRawJsonDisplayText(summaryMetrics),
      });
    }, [summaryMetrics]);

    return (
      <div className="run-overview">
        <Modal
          open={showRaw != null}
          onClose={() => setShowRaw(undefined)}
          size="fullscreen"
          dimmer="blurring"
          closeIcon>
          <Modal.Header>{showRaw?.header}</Modal.Header>
          <Modal.Content>
            <Tailwind>
              <pre className="max-h-[70vh] overflow-auto">{showRaw?.json}</pre>
              <div className="mt-8 flex gap-4">
                <Button onPress={() => setShowRaw(undefined)}>Close</Button>
                <Button
                  variant="secondary"
                  startIcon="copy"
                  onPress={() => copyToClipboard(showRaw?.json)}>
                  Copy
                </Button>
              </div>
            </Tailwind>
          </Modal.Content>
        </Modal>
        <div className="run-overview-top">
          {project.isBenchmark && benchmarkRun && (
            <BenchmarkApproval
              summaryMetrics={benchmarkSummaryMetrics}
              benchmark={project}
              benchmarkRun={benchmarkRun}
            />
          )}
          <JobDrawer
            open={jobToRun != null}
            closeDrawer={closeDrawer}
            entityName={project.entityName}
            projectName={project.name}
            defaultJobId={jobToRun ?? ''}
            defaultJobVersion={jobInfo?.versionStr ?? ''}
            defaultRunToClone={run.name}
          />
          <RunPageHeader
            readOnly={readOnly}
            project={project}
            run={run}
            patchRun={patchRun}
            deleteRun={deleteRun}
            jobId={jobInfo?.id}
            cloneRun={cloneRun}
          />
          <div className="overview-item">
            <div className="overview-key">Notes</div>
            <div className="overview-value">
              <InlineMarkdownEditor
                readOnly={readOnly}
                placeholder="What makes this run special?"
                serverText={notes}
                onBlur={newNotes => {
                  patchRun({notes: newNotes});
                }}
              />
            </div>
          </div>
          {project.isBenchmark && benchmarkRun && (
            // This run is a submitted copy
            <div className="overview-item">
              <div className="overview-key">Source</div>
              <div className="overview-value">
                <Dropdown
                  simple
                  className="dropdown-menu-icon-button benchmark-dropdown"
                  icon={null}
                  disabled={!benchmarkRun.originalProject}
                  trigger={
                    <>
                      <Icon name="folder-alt" />
                      Submitted from{' '}
                      {benchmarkRun.originalProject
                        ? benchmarkRun.originalProject.name
                        : 'deleted project'}
                      <Icon name="chevron-down" />
                    </>
                  }>
                  <Dropdown.Menu>
                    {benchmarkRun.originalProject && (
                      <Link
                        className="item"
                        to={urls.benchmarkOriginalProject(benchmarkRun)}>
                        Visit original project
                      </Link>
                    )}
                    {benchmarkRun.originalRun && (
                      <Link
                        className="item"
                        to={urls.benchmarkOriginalRunOverview(benchmarkRun)}>
                        Visit original run
                      </Link>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          )}
          {(tags.length > 0 || !readOnly) && (
            <Tailwind>
              <div className="overview-item mt-4">
                <div className="overview-key">Tags</div>
                <div className="overview-value">
                  <div className="flex flex-wrap items-center gap-4">
                    {tags.map(tag => (
                      <RemovableTag
                        key={tag.name}
                        label={tag.name}
                        color={getTagContrastColor(tag.colorIndex)}
                        removeAction={
                          <RemoveAction
                            onClick={(e: React.SyntheticEvent) => {
                              e.stopPropagation();
                              patchRun({
                                tags: tags
                                  .map(t => t.name)
                                  .filter(n => n !== tag.name),
                              });
                            }}
                          />
                        }
                      />
                    ))}
                    {!readOnly && (
                      <TagAddButton
                        compact
                        direction="bottom right"
                        tags={tags}
                        availableTags={project.tags}
                        addTag={async tag => {
                          if (
                            !_.includes(
                              tags.map(t => t.name),
                              tag.name
                            )
                          ) {
                            patchRun({
                              tags: [...tags.map(t => t.name), tag.name],
                            });
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </Tailwind>
          )}
          <div className="overview-item">
            <div className="overview-key">Author</div>
            <div className="overview-value">
              {checkIsServiceAccount(user.accountType) ? (
                <div className="user-link">
                  {authorImage}
                  <ServiceAccountModal
                    serviceAccountName={user.name}
                    teamMembersUrl={
                      readOnly ? undefined : serviceAccountModalLink
                    }
                  />
                </div>
              ) : (
                <Link className="user-link" to={`/${user.username}`}>
                  {authorImage}
                  {user.username}
                </Link>
              )}
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-key">State</div>
            <div className="overview-value">
              <RunStateIndicator run={run} readOnly={readOnly} />
            </div>
          </div>
          {jobInfo != null && (
            <div className="overview-item">
              <div className="overview-key">Job</div>
              <Link2
                to={urls.job({
                  entityName: project.entityName,
                  projectName: project.name,
                  jobId: jobInfo.id,
                  jobVersion: jobInfo.versionStr,
                  jobTab: 'version_details',
                })}>
                <div className="overview-value">
                  {jobInfo?.versionedJobName}
                </div>
              </Link2>
            </div>
          )}
          {branchPoint && (
            <div className="overview-item">
              <div className="overview-key">Forked From</div>
              <Link
                to={urls.run({
                  entityName: branchPoint.run.project.entity.name,
                  projectName: branchPoint.run.project.name,
                  name: branchPoint.run.name,
                })}>
                <div className="overview-value">
                  {branchPoint.run.name} @ Step {branchPoint.step}
                </div>
              </Link>
            </div>
          )}
          <div className="overview-item">
            <div className="overview-key">Start time</div>
            <div className="overview-value">{createdTime}</div>
          </div>
          {sweep != null && (
            <div className="overview-item">
              <div className="overview-key">Sweep</div>
              <Link
                to={urls.sweep({
                  entityName: project.entityName,
                  projectName: project.name,
                  sweepName: sweep.name,
                })}>
                <div className="overview-value">{sweep.name}</div>
              </Link>
            </div>
          )}
          <div className="overview-item">
            <div className="overview-key">
              Runtime
              <InfoTooltip
                id="runtime-tooltip"
                aria-label="Run duration (including idle time)">
                Total time from when the run started to when it last reported
                activity. May include idle time.
              </InfoTooltip>
            </div>
            <div className="overview-value" aria-describedby="runtime-tooltip">
              <TimeDisplay
                timestamp={new Date(run.createdAt)}
                format="month_round"
                now={new Date(run.heartbeatAt)}
              />
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-key">
              Tracked hours
              <InfoTooltip
                id="tracked-hours-tooltip"
                aria-label="Active compute time (billable)">
                Total time using active compute — used for billing. May be less
                than runtime if the run was idle.
              </InfoTooltip>
            </div>
            <div
              className="overview-value"
              aria-describedby="tracked-hours-tooltip">
              {run.computeSeconds ? monthRoundedTime(run.computeSeconds) : '-'}
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-key">Run path</div>
            <div className="overview-value">
              <CopyableText
                className="run-path"
                text={`${project.entityName}/${project.name}/${name}`}
                toastText="Successfully copied run path"
              />
            </div>
          </div>
          {host !== null && (
            <div className="overview-item">
              <div className="overview-key">Hostname</div>
              <div className="overview-value">{host}</div>
            </div>
          )}
          {project.linkedBenchmark && viewer && (
            <div className="overview-item">
              <div className="overview-key">Benchmark</div>
              <div className="overview-value">
                <TargetBlank
                  className="benchmark-link"
                  href={urls.project(project.linkedBenchmark)}>
                  View Benchmark project
                  <IconOpenNewTab />
                </TargetBlank>
              </div>
            </div>
          )}
          {runInfo && (
            <>
              <div className="overview-item">
                <div className="overview-key">OS</div>
                <div className="overview-value">{runInfo.os || '-'}</div>
              </div>
              <div className="overview-item">
                <div className="overview-key">Python version</div>
                <div className="overview-value">{runInfo.python || '-'}</div>
              </div>
              {runInfo.executable && (
                <div className="overview-item">
                  <div className="overview-key">Python executable</div>
                  <div className="overview-value">{runInfo.executable}</div>
                </div>
              )}
              {runInfo.colab && (
                <div className="overview-item">
                  <div className="overview-key">Colab link</div>
                  <div className="overview-value">
                    <TargetBlank href={runInfo.colab}>
                      {runInfo.colab}
                    </TargetBlank>
                  </div>
                </div>
              )}
              {runInfo.git && (
                <>
                  <div className="overview-item">
                    <div className="overview-key">Git repository</div>
                    <div className="overview-value command">
                      {runInfo.git.remote !== '' && (
                        <>git clone {runInfo.git.remote}</>
                      )}
                    </div>
                  </div>
                  <div className="overview-item">
                    <div className="overview-key">Git state</div>
                    <div className="overview-value command">
                      {runInfo.git.commit !== '' && (
                        <>
                          git checkout -b "
                          {sanitizeBranchName(displayName, '-')}"{' '}
                          {runInfo.git.commit}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="overview-item">
                <div className="overview-key">Command</div>
                <div className="overview-value command">
                  {runInfo.program ? (
                    <div>
                      {runInfo.program +
                        ' ' +
                        (runInfo.args && !runInfo.program.includes('.ipynb')
                          ? runInfo.args
                              .map(a => {
                                // Quote the argument
                                if (a.match(/[\s|&<>]/g)) {
                                  let prefix = '';
                                  // Check if the arg is like --foo=bla bla
                                  if (a.match(/^[\w-]+=/)) {
                                    const parts = a.split('=');
                                    prefix = parts.shift() + '=';
                                    a = parts.join('=');
                                  }
                                  return `${prefix}"${a.replace(/"/g, '\\"')}"`;
                                }
                                return a;
                              })
                              .join(' ')
                          : '')}
                    </div>
                  ) : (
                    '-'
                  )}
                </div>
              </div>
              {(runInfo.cpuCount ||
                runInfo.gpuCount ||
                runInfo.gpu ||
                runInfo.cpuCountLogical) && (
                <div
                  style={
                    applySystemHardwareStyles
                      ? {alignItems: 'flex-start', marginTop: '4px'}
                      : undefined
                  }
                  className="overview-item">
                  <div className="overview-key">System Hardware</div>
                  <div className="overview-value">
                    <table className="system-table">
                      <tbody>
                        {runInfo.cpuCount && (
                          <tr>
                            <td className="key">CPU count</td>
                            <td className="val">{runInfo.cpuCount}</td>
                          </tr>
                        )}
                        {runInfo.cpuCountLogical && (
                          <tr>
                            <td className="key">Logical CPU count </td>
                            <td className="val">{runInfo.cpuCountLogical}</td>
                          </tr>
                        )}
                        {runInfo.gpuCount && (
                          <tr>
                            <td className="key">GPU count</td>
                            <td className="val">{runInfo.gpuCount}</td>
                          </tr>
                        )}
                        {runInfo.gpu && (
                          <tr>
                            <td className="key">GPU type</td>
                            <td className="val">{runInfo.gpu}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
          {cliVersion && (
            <div className="overview-item">
              <div className="overview-key">W&amp;B CLI Version</div>
              <div className="overview-value">{cliVersion}</div>
            </div>
          )}
          {tracesLink && (
            <div className="overview-item">
              <div className="overview-key">Weave traces</div>
              <Link2 className="overview-value" to={tracesLink}>
                {traceCount.toLocaleString()}{' '}
                {maybePluralizeWord(traceCount, 'trace')}
              </Link2>
            </div>
          )}
          {spellUrl && (
            <div className="overview-item">
              <div className="overview-key">Spell Run</div>
              <div className="overview-value">
                <TargetBlank href={`${spellUrl}`}>{spellUrl}</TargetBlank>
              </div>
            </div>
          )}
          {group && (
            <div className="overview-item">
              <div className="overview-key">Group</div>
              <Link
                className="overview-value"
                to={urls.runGroup({
                  entityName: project.entityName,
                  projectName: project.name,
                  name: group,
                })}>
                {group}
              </Link>
            </div>
          )}
          {jobType && (
            <div className="overview-item">
              <div className="overview-key">Job Type</div>
              <div className="overview-value">{jobType}</div>
            </div>
          )}
          {isParquetExportAllowed(isAdmin) && (
            <div className="overview-item">
              <div className="overview-key">Parquet Export</div>
              <div className="overview-value">
                <RunTriggerParquetExport runID={run.id} />
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="overview-item">
              <div className="overview-key">Recompute KeysInfo</div>
              <div className="overview-value">
                <RunTriggerRecomputeKeysInfo runID={run.id} />
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="overview-item">
              <div className="overview-key">Trigger Export Events</div>
              <div className="overview-value">
                <RunTriggerEventsExport runID={run.id} />
              </div>
            </div>
          )}
        </div>
        <Grid stackable className="config-and-summary">
          <Grid.Row columns={2}>
            <Grid.Column>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                }}>
                <Header>Config</Header>
                <Button
                  size="sm"
                  variant="secondary"
                  onPress={() =>
                    setShowRaw({
                      header: `Config`,
                      json: getRawJsonDisplayText(config),
                    })
                  }>
                  View raw data
                </Button>
              </span>
              <p className="hint-text">
                Config parameters are your model's inputs.{' '}
                <TargetBlank href={DOC_URLS.config}>Learn more</TargetBlank>
              </p>
              <RunConfigTree
                config={config}
                projectName={project.name}
                entityName={project.entityName}
                userID={viewer?.id ?? ''}
              />
            </Grid.Column>
            <Grid.Column>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                }}>
                <Header>Summary</Header>
                <Button
                  size="sm"
                  variant="secondary"
                  onPress={() =>
                    setShowRaw({
                      header: `Summary`,
                      json: getRawJsonDisplayText(summaryMetrics),
                    })
                  }>
                  View raw data
                </Button>
              </span>
              <p className="hint-text">
                Summary metrics are your model's outputs.{' '}
                <TargetBlank href={DOC_URLS.summaryMetric}>
                  Learn more
                </TargetBlank>
              </p>
              {_.keys(summaryMetrics).length === 0 ? (
                <Segment>
                  <p>No summary metrics saved for this run.</p>
                  <p>
                    Check the{' '}
                    <TargetBlank href={DOC_URLS.summaryMetric}>
                      summary metrics documentation
                    </TargetBlank>{' '}
                    for more information.
                  </p>
                </Segment>
              ) : (
                <RunSummaryTree
                  projectName={project.name}
                  entityName={project.entityName}
                  userID={viewer?.id ?? ''}
                  summary={summaryMetrics}
                  viewRawData={viewRawSummary}
                />
              )}
            </Grid.Column>
          </Grid.Row>
          <RunArtifactGrid
            entityName={project.entityName}
            projectName={project.name}
            runName={run.name}
          />
        </Grid>
      </div>
    );
  }
);
RunOverview.displayName = 'RunOverview';

const getRawJsonDisplayText = (json: any): string => {
  const stringifiedJson = JSON.stringify(json);

  return stringifiedJson.length < 1000000
    ? JSON.stringify(json, null, 2)
    : stringifiedJson;
};

export default RunOverview;
